/**
 * Exportuje textový obsah sekce (tabulka nebo časový režim startovky) do .txt souboru.
 * Generuje pouze prvních 5 sloupců a zarovná 3. sloupec tak, aby vždy začínal na stejné pozici.
 *
 * @param {string} elementId  ID root elementu s obsahem
 * @param {string} filename   název výsledného .txt souboru
 * @param {string} delimiter  oddělovač sloupců (výchozí '\t')
 * @param {string[]} headerLines řádky, které se vypíšou úplně nahoře (volitelné)
 */
export function exportToTxt(elementId, filename = 'export.txt', delimiter = '\t', headerLines = []) {
  const root = document.getElementById(elementId);
  if (!root) {
    return;
  }

  const lines = [];
  const hdr = Array.isArray(headerLines) ? headerLines : [];
  const hdrClean = hdr.map(x => (x == null ? '' : String(x).trim())).filter(Boolean);
  if (hdrClean.length) {
    lines.push(...hdrClean);
    lines.push('');
  }

  // 1) pokud jsou v rootu <table>, parsujeme je
  const tables = Array.from(root.querySelectorAll('table'));
  if (tables.length > 0) {
    tables.forEach(table => {
      // nadpis kategorie před tabulkou
      const section = table.closest('.v-col');
      const titleEl = section && section.querySelector('.v-card-title');
      if (titleEl) {
        lines.push(titleEl.innerText.trim());
      }

      // data řádky (přes <tbody>)
      Array.from(table.querySelectorAll('tbody tr')).forEach(tr => {
        const cells = Array.from(tr.querySelectorAll('td'))
          .map(td => td.innerText.trim().replace(/\r?\n+/g, ' '));
        lines.push(formatRow(cells, delimiter));
      });

      lines.push(''); // prázdný řádek mezi sekcemi
    });

  } else if (root.querySelector('.startlist-header-row')) {
    // 2) časový režim startovky — bez hlavičky, jen skupiny podle času
    const groups = Array.from(root.querySelectorAll(`#${elementId} > .v-row > .v-col`))
      .filter(col => col.querySelector('.startlist-row'));

    groups.forEach(col => {
      // čas jako nadpis skupiny
      const timeTitle = col.querySelector('.v-card-title');
      if (timeTitle) {
        lines.push(timeTitle.innerText.trim());
      }
      // položky v rámci skupiny
      Array.from(col.querySelectorAll('.startlist-item .startlist-row')).forEach(row => {
        const cells = Array.from(row.querySelectorAll('.startlist-cell'))
          .map(cell => cell.innerText.trim());
        lines.push(formatRow(cells, delimiter));
      });
      lines.push(''); // oddělení skupin
    });

  } else {
    console.warn('Nenalezena ani tabulka, ani flex‐startlist.');
    return;
  }

  // vytvoření a stažení TXT (Windows-1250)
  const content = lines.join('\n');
  const bytes = encodeWindows1250(content);
  const blob = new Blob([bytes], { type: 'text/plain;charset=windows-1250' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Sestaví řádek z prvních 5 buněk:
 *  - sloupce 1 a 2 oddělí delimiterem
 *  - za ně doplní mezery tak, aby 3. sloupec vždy začínal na 32. znaku
 *  - poté připojí delimiter + 3., 4. a 5. sloupec bez dalších odsazení
 */
function formatRow(cells, delimiter) {
  const [c1 = '', c2 = '', c3 = '', c4 = '', c5 = ''] = cells;
  const firstPart = `${c1}${delimiter}${c2}`;
  // zarovnáme na pevnou délku 32 znaků
  const padded = firstPart.padEnd(32, ' ');
  return (
    `${padded}` +
    `${delimiter}${c3}` +
    `${delimiter}${delimiter}${c4}` + 
    `${delimiter}${c5}`   
  );
}

/**
 * Minimal Windows-1250 encoder for Central Europe.
 * - ASCII passes through
 * - Common Czech/Slovak/PL/HU diacritics are mapped
 * - Unsupported chars become '?'
 */
function encodeWindows1250(str) {
  const map = {
    '€': 0x80, '‚': 0x82, '„': 0x84, '…': 0x85, '†': 0x86, '‡': 0x87, '‰': 0x89,
    'Š': 0x8A, '‹': 0x8B, 'Ś': 0x8C, 'Ť': 0x8D, 'Ž': 0x8E, 'Ź': 0x8F,
    '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97, '™': 0x99,
    'š': 0x9A, '›': 0x9B, 'ś': 0x9C, 'ť': 0x9D, 'ž': 0x9E, 'ź': 0x9F,
    ' ': 0xA0, 'ˇ': 0xA1, '˘': 0xA2, 'Ł': 0xA3, '¤': 0xA4, 'Ą': 0xA5, '¦': 0xA6, '§': 0xA7,
    '¨': 0xA8, '©': 0xA9, 'Ş': 0xAA, '«': 0xAB, '¬': 0xAC, '­': 0xAD, '®': 0xAE, 'Ż': 0xAF,
    '°': 0xB0, '±': 0xB1, '˛': 0xB2, 'ł': 0xB3, '´': 0xB4, 'ą': 0xB5, '¸': 0xB8,
    'ś': 0x9C, 'Ş': 0xAA, 'ş': 0xBA, '»': 0xBB, 'Ľ': 0xBC, '˝': 0xBD, 'ľ': 0xBE, 'ż': 0xBF,
    'Ŕ': 0xC0, 'Á': 0xC1, 'Â': 0xC2, 'Ă': 0xC3, 'Ä': 0xC4, 'Ĺ': 0xC5, 'Ć': 0xC6, 'Ç': 0xC7,
    'Č': 0xC8, 'É': 0xC9, 'Ę': 0xCA, 'Ë': 0xCB, 'Ě': 0xCC, 'Í': 0xCD, 'Î': 0xCE, 'Ď': 0xCF,
    'Đ': 0xD0, 'Ń': 0xD1, 'Ň': 0xD2, 'Ó': 0xD3, 'Ô': 0xD4, 'Ő': 0xD5, 'Ö': 0xD6, '×': 0xD7,
    'Ř': 0xD8, 'Ú': 0xD9, 'Ű': 0xDA, 'Ü': 0xDB, 'Ý': 0xDD, 'Ţ': 0xDE, 'ß': 0xDF,
    'ŕ': 0xE0, 'á': 0xE1, 'â': 0xE2, 'ă': 0xE3, 'ä': 0xE4, 'ĺ': 0xE5, 'ć': 0xE6, 'ç': 0xE7,
    'č': 0xE8, 'é': 0xE9, 'ę': 0xEA, 'ë': 0xEB, 'ě': 0xEC, 'í': 0xED, 'î': 0xEE, 'ď': 0xEF,
    'đ': 0xF0, 'ń': 0xF1, 'ň': 0xF2, 'ó': 0xF3, 'ô': 0xF4, 'ő': 0xF5, 'ö': 0xF6, '÷': 0xF7,
    'ř': 0xF8, 'ú': 0xFA, 'ű': 0xFB, 'ü': 0xFC, 'ý': 0xFD, 'ţ': 0xFE,
  };

  const out = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    const code = ch.codePointAt(0);
    if (code <= 0x7F) {
      out[i] = code;
    } else if (Object.prototype.hasOwnProperty.call(map, ch)) {
      out[i] = map[ch];
    } else {
      out[i] = 0x3F; // '?'
    }
  }
  return out;
}

